import * as React from 'react'

import getConfig from 'next/config'

import { Products as ProductsApi } from '@grandvisionhq/api-client'
import {
  CmsComponentSlot,
  CmsHeadTags,
  CmsProductBreadcrumbs,
  useBloomreachPageRoutes,
  useBloomreachPdpContent,
  useBloomreachProductRoutes,
} from '@grandvisionhq/bloomreach'
import { MiniCartModal, Upsell } from '@grandvisionhq/carts-v2'
import { CommerceToolsApi } from '@grandvisionhq/commercetools'
import { VtoConfigProvider } from '@grandvisionhq/fun-products'
import { NextPage, withErrorPage } from '@grandvisionhq/next'
import {
  getProductSeoInfo,
  MiniCartProductRecommendations,
  parseProductDetailQueryParams,
  ProductDetail,
  ProductDetailProvider,
  ProductFrameMeasurements,
  ProductStructuredDataObject,
} from '@grandvisionhq/products'
import { ReviewDisplay, ReviewsProvider } from '@grandvisionhq/reviews'
import { CrossSellProducts } from '@grandvisionhq/search'
import { DeliveryIndicatorProvider } from '@grandvisionhq/shipping'
import { useFeatureToggles, useIntl } from '@grandvisionhq/state'
import { compose, getAttrValue, isString } from '@grandvisionhq/utils'

import { withCms, withLayout } from '~/cms'
import { defaultErrorProps, ErrorProps } from '~/components/error-props'
import { settings as cartSettings, withCart } from '~/components/hocs/with-cart'
import { SSP } from '~/components/SSP'
import { getDeliveryIndicatorConfig } from '~/configuration'
import { getAlgoliaCrossSellConfig } from '~/util/algolia'
import { deliveryResolverFactory } from '~/util/delivery-resolver-factory'

import '@grandvisionhq/reviews/dist/index.css'

const { publicRuntimeConfig } = getConfig()
const endpoints = publicRuntimeConfig.grandvision.endpoints || {}
const staticsUrl = publicRuntimeConfig.grandvision.staticsUrl || ''
const selfUrl = publicRuntimeConfig.selfUrl || ''
const vtoConfig = publicRuntimeConfig.ditto
const { apiKey: powerReviewsApiKey, groupId, opcoId } = publicRuntimeConfig.powerReviews

const deliveryIndicatorConfig = getDeliveryIndicatorConfig()

type ReviewProviderProps = React.ComponentProps<typeof ReviewsProvider>

type InitialProps = {
  sku: string
  product?: CommerceToolsApi.ProductProjection
  queryParams: Record<string, string | string[]>
}

type Props = InitialProps

export const ProductDetailPage: NextPage<Props, InitialProps | ErrorProps> = ({
  sku,
  product,
  queryParams,
}) => {
  const { locale } = useIntl()
  const content = useBloomreachPdpContent()
  const { routes } = useBloomreachPageRoutes()
  const [, { formatProductUrl }] = useBloomreachProductRoutes()
  const { isBynderImagesEnabled, isCrossSellEnabled, isReviewsEnabled } = useFeatureToggles()

  /**
   * Product data, seo, stock, delivery, cross-sell configurations
   */
  const productType = product?.productType?.obj?.name as CommerceToolsApi.ProductTypeName
  const productUrl = `${selfUrl}${formatProductUrl({
    slug: product?.slug ?? {},
    productType,
    sku,
  })}`
  const brandKey = String(getAttrValue(product?.masterVariant ?? {}, 'brandKey'))
  const seoInfo = getProductSeoInfo(staticsUrl, locale, product, undefined, isBynderImagesEnabled)
  const specifications = React.useMemo(() => getSpecifications(productType), [productType])
  const deliveryResolver = React.useMemo(
    () => deliveryResolverFactory([product], productType),
    [product, productType]
  )
  const crossSellConfig = React.useMemo(() => getAlgoliaCrossSellConfig(productType), [productType])

  /**
   * Product type checks
   */
  const isLenses = React.useMemo(() => CommerceToolsApi.isLenses(productType), [productType])
  const isSunglasses = React.useMemo(
    () => CommerceToolsApi.isSunglasses(productType),
    [productType]
  )
  const isFrames = React.useMemo(() => CommerceToolsApi.isFrames(productType), [productType])

  /**
   * PowerReviews setup
   */
  const reviewsInitialId = isLenses ? brandKey : product?.masterVariant.sku
  const reviewsInitialSku = isLenses ? undefined : sku // SKU is not required for CL
  const reviewsProviderProps = React.useMemo<ReviewProviderProps>(
    () => ({
      enabled: isReviewsEnabled && !!product?.reviewEnabled,
      config: {
        groupId,
        opcoId,
        apiKey: powerReviewsApiKey,
        writeReviewPath: routes['write-review'],
      },
    }),
    [product, routes]
  )

  if (!product) {
    // tslint:disable-next-line:no-console
    console.error(`Failed to render ProductDetailPage because of invalid product data: ${product}`)
    return null
  }

  /**
   * Cart v2 upsell setup (optional)
   */
  const productCatKey = getAttrValue(product.masterVariant, 'recommendedProductCatKey')
  const recommendedCategoryKey = isString(productCatKey) ? productCatKey : undefined
  const params = parseProductDetailQueryParams(queryParams)

  const subscriptionSku = getAttrValue(product.masterVariant, 'subscriptionProductSKU')
  const subscriptionProductSku = isString(subscriptionSku) ? subscriptionSku : undefined

  return (
    <>
      <CmsHeadTags
        title={seoInfo.title}
        description={seoInfo.description}
        ogImage={seoInfo.imageUrl}
        ogUrl={productUrl}
      >
        {isReviewsEnabled && <link rel="preconnect" href="https://ui.powerreviews.com" />}
      </CmsHeadTags>
      <CmsComponentSlot path="main/top-container" />
      <DeliveryIndicatorProvider config={deliveryIndicatorConfig}>
        <ProductDetailProvider
          {...params}
          locale={locale}
          product={product}
          productType={productType}
          sku={sku}
          routes={routes}
          staticsUrl={staticsUrl}
        >
          <CmsProductBreadcrumbs product={product} sku={sku} />
          <ReviewsProvider {...reviewsProviderProps}>
            <VtoConfigProvider config={vtoConfig}>
              <ProductDetail
                allowBackOrder={true}
                brandPromotions={content.brandPromotions}
                deliveryResolver={deliveryResolver}
                disclaimer={content.disclaimer}
                formatProductUrl={formatProductUrl}
                maxOrderableQuantity={cartSettings.maximumQuantity}
                configuratorDocument={content.configuratorDocument}
                prescriptionModalContent={content.modalContent}
                productType={productType}
                productTypeUsps={content.usps}
                specificationConfiguration={specifications}
                vtoBanner={content.vtoBanner}
              />
            </VtoConfigProvider>

            {isFrames && (
              <React.Fragment>
                <a id="bestelOnline" href="#here">
                  a
                </a>
                <SSP id="pnl" product={product} />
              </React.Fragment>
            )}

            {(isSunglasses || isFrames) && <ProductFrameMeasurements product={product} />}

            {isCrossSellEnabled && (
              <CrossSellProducts
                algoliaConfig={crossSellConfig}
                formatProductUrl={formatProductUrl}
              />
            )}

            {isReviewsEnabled && (
              <ReviewDisplay productId={reviewsInitialId} variantId={reviewsInitialSku} />
            )}

            <div className="product-detail-layout__components">
              <CmsComponentSlot path="main/bottom-container" />
            </div>

            <MiniCartModal links={{ cart: routes.cart, checkout: routes.checkout }}>
              {recommendedCategoryKey || subscriptionProductSku
                ? (addedLineItems) => (
                    <Upsell
                      recommendedCategoryKey={recommendedCategoryKey}
                      subscriptionProductSku={subscriptionProductSku}
                      lineItems={addedLineItems}
                    />
                  )
                : (addedLineItems) => (
                    <MiniCartProductRecommendations
                      recommendationContext={
                        addedLineItems.some(({ type }) => type === 'prescription-glasses')
                          ? 'MINICART_RX'
                          : 'MINICART'
                      }
                      skus={addedLineItems.map((l) =>
                        l.type === 'prescription-glasses' ? l.frame.sku : l.sku
                      )}
                      formatProductUrl={formatProductUrl}
                    />
                  )}
            </MiniCartModal>

            <ProductStructuredDataObject
              locale={locale}
              product={product}
              staticsUrl={staticsUrl}
              url={productUrl}
            />
          </ReviewsProvider>
        </ProductDetailProvider>
      </DeliveryIndicatorProvider>
    </>
  )
}

ProductDetailPage.getInitialProps = async ({ query, asPath = '' }) => {
  const [, slug, sku] = /.+\/(.+)\/([^?]+)(\?.*)?$/.exec(asPath) ?? []

  if (!slug) {
    throw new Error(`Can't extract slug from ${asPath}, slug: '${String(slug)}'`)
  }

  let product: CommerceToolsApi.ProductProjection | undefined
  try {
    product = await ProductsApi.getProductBySlug(endpoints.products, { slug })
  } catch (err: any) {
    // Non OK product service api response, return props to show the error page
    // tslint:disable-next-line:no-console
    console.error(`Failed to fetch product with slug: ${String(slug)}`, err)
    return {
      ...defaultErrorProps,
      statusCode: err.statusCode,
    }
  }

  return {
    product,
    sku,
    queryParams: query as Record<string, string | string[]>,
  }
}

type Specification = {
  labelKey: string
  attribute: string
  isLocalised?: boolean
  isSet?: boolean
}

function getSpecifications(productType: string): Specification[] | undefined {
  if (productType === 'Frames') {
    return [
      {
        labelKey: 'products.specifications.brand',
        attribute: 'brand',
      },
      {
        labelKey: 'products.specifications.frameShape',
        attribute: 'frameShape',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.frameColor',
        attribute: 'frameColor',
        isLocalised: true,
        isSet: true,
      },
      {
        labelKey: 'products.specifications.frameMaterial',
        attribute: 'frameMaterial',
        isLocalised: true,
        isSet: true,
      },
      {
        labelKey: 'products.specifications.templeType',
        attribute: 'templeType',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.articleNumber',
        attribute: 'opcoBackendId',
      },
      {
        labelKey: 'products.specifications.trend',
        attribute: 'trend',
      },
    ]
  }

  if (productType === 'Sunglasses') {
    return [
      {
        labelKey: 'products.specifications.brand',
        attribute: 'brand',
      },
      {
        labelKey: 'products.specifications.frameColor',
        attribute: 'frameColor',
        isLocalised: true,
        isSet: true,
      },
      {
        labelKey: 'products.specifications.lensColor',
        attribute: 'lensColor',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.frameShape',
        attribute: 'frameShape',
        isLocalised: true,
      },
      {
        labelKey: 'products.sunglasses.features.polarized',
        attribute: 'polarized',
      },
      {
        labelKey: 'products.specifications.prescriptionGlass',
        attribute: 'prescriptionGlass',
      },
      {
        labelKey: 'products.specifications.frameMaterial',
        attribute: 'frameMaterial',
        isLocalised: true,
        isSet: true,
      },
      {
        labelKey: 'products.specifications.templeType',
        attribute: 'templeType',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.articleNumber',
        attribute: 'opcoBackendId',
      },
      {
        labelKey: 'products.specifications.trend',
        attribute: 'trend',
      },
    ]
  }
}

export default compose(
  withCms(),
  withErrorPage(),
  withLayout(),
  withCart({ readOnly: true })
)(ProductDetailPage)
